export type InstrumentCode = string;
export type ExchangeId = string;
export type QuoteKey = {
  instrumentCode: InstrumentCode;
  exchangeId: ExchangeId;
};

export enum PriceMovement {
  Up = 'Up',
  Down = 'Down',
  NoChange = 'NoChange',
  Unknown = 'Unknown'
}

export enum DisplayPriceType {
  Unknown = 'Unknown',
  Last = 'Last',
  Close = 'Close',
  Indicative = 'Indicative',
  Delayed = 'Delayed'
}

export enum TradingState {
  Unknown = 'Unknown',
  AbbAuction = 'AbbAuction',
  Adjust = 'Adjust',
  AdjustOn = 'AdjustOn',
  Close = 'Close',
  Cspa = 'Cspa',
  Enquire = 'Enquire',
  LateTrading = 'LateTrading',
  Open = 'Open',
  OpenNightTrading = 'OpenNightTrading',
  PreCspa = 'PreCspa',
  PreNightTrading = 'PreNightTrading',
  PreNr = 'PreNr',
  PreOpen = 'PreOpen',
  PurgeOrders = 'PurgeOrders',
  Suspend = 'Suspend',
  TradingHalt = 'TradingHalt',
  InOpen = 'InOpen',
  AfterHoursAdjust = 'AfterHoursAdjust',
  RegulatoryHalt = 'RegulatoryHalt',
  OpenQuoteDisplay = 'OpenQuoteDisplay',
  OpenVmb = 'OpenVmb',
  WaitVmb = 'WaitVmb',
  InternationalHalt = 'InternationalHalt',
  PreTrade = 'PreTrade',
  PostTrade = 'PostTrade'
}

export enum CorporateAction {
  CumBonus = 'CB',
  CumCapital = 'CC',
  ShortSellAllowed = 'SH',
  CurrentlyUnderReconstruction = 'RD',
  Suspended = 'SU',
  SpecialDistribution = 'SD',
  CumDividend = 'CD',
  CumEntitlement = 'CE',
  CumTakeoverOffer = 'CF',
  CumInterest = 'CI',
  CallDue = 'CL',
  CumPremiumReturn = 'CM',
  CallPaid = 'CP',
  CumEqualAccessBuyback = 'CQ',
  CumRights = 'CR',
  ConditionalTrading = 'CT',
  CumPriority = 'CZ',
  LackingCompliance = 'LC',
  NoticeReceivedafter4pmpreviousday = 'NL',
  NoticePending = 'NP',
  NoticeReceived = 'NR',
  UnderOfferofTakeover = 'NS',
  NewExInterest = 'NX',
  ProtectionAvailable = 'PA',
  ProtectionUnavailable = 'PU',
  ReceiverAppointed = 'RA',
  Reconstructed = 'RE',
  StockExchangeQuery = 'SE',
  TradingHalt = 'TH',
  ExBonusIssue = 'XB',
  ExReturnofCapital = 'XC',
  ExDividend = 'XD',
  ExEntitlement = 'XE',
  ExTakeoverOffer = 'XF',
  ExInterest = 'XI',
  ExReturnofPremium = 'XM',
  ExEqualAccessBuyback = 'XQ',
  ExRightsIssue = 'XR',
  ExPriority = 'XZ'
}

export interface IInstrumentStatus {
  code: TradingState;
  name: string;
  description: string;
}

export interface IInstrumentCorporateAction {
  code: CorporateAction;
  name: string;
  description: string;
}

export interface IInstrumentExtendedTrading {
  lastPrice: number;
  priceChange: number;
  priceChangePercent: number;
  totalVolumeTraded: number;
}

export interface IExchangeInstrumentQuote {
  hash: string;

  currency: string;
  country: string;
  bestBuyPrice: number;
  bestSellPrice: number;
  bids: number;
  bidsTotalVolume: number;
  closingPrice: number;
  hasValidPricing: boolean;
  highSalePrice: number;
  indicativePrice: number;
  lastPriceMovement: PriceMovement;
  lastYield: number;
  lowSalePrice: number;
  status: IInstrumentStatus;
  corporateActions: IInstrumentCorporateAction[];
  matchVolume: number;
  midPrice: number;
  offers: number;
  offersTotalVolume: number;
  openingPrice: number;
  openInterest: number;
  vwapPrice: number;
  totalVolumeTraded: number;
  totalValueTraded: number;
  totalNumberOfTrades: number;
  surplusVolume: number;
  isTradeable: boolean;
  brokerageScript: string;
  // statusUi: string; from status and corporate actions
  extendedTrading: IInstrumentExtendedTrading;
  lastPrice: number;
  displayPrice: number;
  displayPriceType: string;
  priceChange: number;
  priceChangePercent: number;
  decimalPrecisionForDisplay: number;
  delayed: boolean;
  quoteTime: number;
  lastUpdated: number;
  lastUpdatedApi: number;
}

export interface IInstrumentQuote {
  code: InstrumentCode;
  exchanges: Record<ExchangeId, IExchangeInstrumentQuote>;
}

export type AccountNo = string;
export type CurrencyCode = string;

export interface IFxRate {
  bid: number;
  offer: number;
  unavailable: boolean;
  error?: string;
  lastUpdated: number;
}

export type FxRateKey = { accountNo: AccountNo; fromCurrencyCode: CurrencyCode; toCurrencyCode: CurrencyCode };
